<template>
  <div>
    <div
      v-for="fieldConfig in getInputConfigs"
      :key="fieldConfig.key"
    >
      <component
        :is="fieldConfig.component"
        :config="fieldConfig"
        @InputValidatorModel="(value) => handleOAuthFieldChange(fieldConfig.key, value)"
      />
      <p
        v-if="fieldConfig.supportText !== ''"
        :class="$style.supportingText"
      >
        {{ fieldConfig.supportText }}
      </p>
    </div>
  </div>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { isOauthScopesString, isSecureUrl } from 'client/applications/utils/ApplicationUtils';
import { mapActions, mapGetters, mapState } from 'vuex';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import TextareaValidator from 'client/components/FormValidation/TextareaValidator.vue';

const { application } = ItemTypeConsts;
const ApplicationModel = StoreModelMap[application];

const REQUIRED = 'required';
const URL = 'url';
const HTTPS = 'isSecureUrl';

export default {
  name: 'OAuthConfigurationSection',

  inject: ['$validator'],

  computed: {
    ...mapState(ApplicationModel, {
      scimIsTestConnectionPassed: 'scimIsTestConnectionPassed',
      idmOauthConfig: 'idmOauthConfig',
    }),

    ...mapGetters(ApplicationModel, [
      'didOauthConfigChanged',
    ]),

    getInputConfigs() {
      const HIDDEN = 'password';

      const {
        authorizationUrl,
        accessTokenUrl,
        revokeUrl,
        scopes,
      } = this.$store.state[ApplicationModel].idmOauthConfig;

      const buildInputConfig = (key, name, value, validate = [REQUIRED, URL, HTTPS].join('|'), type = URL, component = InputValidator, supportText = '') => {
        const label = validate.includes(REQUIRED) ? `${name}*` : name;
        return {
          key, label, name, type, validate, value, component, supportText,
        };
      };

      const configs = [
        buildInputConfig('authorizationUrl', 'Authorization URL', authorizationUrl),
        buildInputConfig('clientId', 'Client ID', '', REQUIRED, HIDDEN),
        buildInputConfig('clientSecret', 'Client Secret', '', REQUIRED, HIDDEN),
        buildInputConfig('accessTokenUrl', 'Access Token URL', accessTokenUrl),
        buildInputConfig('revokeUrl', 'Revoke URL', revokeUrl, [URL, HTTPS].join('|')),
        buildInputConfig(
          'scopes',
          'Scopes',
          scopes,
          'isOauthScopesString',
          'text',
          TextareaValidator,
          'Use a space to separate multiple values',
        ),
      ];

      const { isScimActive, scimIsTestConnectionPassed } = this.$store.state[ApplicationModel];
      const isConnectorConfigured = (isScimActive || scimIsTestConnectionPassed);
      const shouldCredsBeUnrequiredFields = isConnectorConfigured
        && !this.didOauthConfigChanged;

      if (shouldCredsBeUnrequiredFields) {
        configs.splice(1, 1, buildInputConfig('clientId', 'Client ID', '', '', HIDDEN));
        configs.splice(2, 1, buildInputConfig('clientSecret', 'Client Secret', '', '', HIDDEN));
      }

      return configs;
    },
  },

  beforeMount() {
    isOauthScopesString(this.$validator);
    isSecureUrl(this.$validator);
  },

  updated() {
    // Manually trigger validation since
    // GenericScimConnectorConfig.areCredentialsValid
    // is not catching the changes
    this.$validator.validate()
      .then(() => { /* NOOP */ });
  },

  methods: {
    ...mapActions(ApplicationModel, [
      'setOAuthConfigValue',
      'setIdmOauthConfigured',
    ]),

    handleOAuthFieldChange(key, value) {
      const cleanedVal = value.replace(/\s+/g, ' ').trim();
      const payload = {
        ...this.idmOauthConfig,
        [key]: cleanedVal,
      };
      this.setOAuthConfigValue(payload);

      const oauthConfigured = this.scimIsTestConnectionPassed ? !this.didOauthConfigChanged : false;
      this.setIdmOauthConfigured(oauthConfigured);
    },
  },
};
</script>

<style module>
@import '../css/IdentityManagementCommon.css';
</style>
